/**
 * Данные раздела «Сцепки» — react-query поверх `/company/machinery-units`.
 *
 * Ключи вложены в префикс карточки хозяйства: набор операций сцепки меняет и
 * список, и колонку «Операции», и их незачем обновлять порознь.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { farmCardKeys } from '../../queries';
import type { MachineryUnit, UnitsPage } from './unitsLogic';

export const unitKeys = {
  all: (farmId: string) => [...farmCardKeys.all(farmId), 'machinery-units'] as const,
  list: (farmId: string, search: string) =>
    [...farmCardKeys.all(farmId), 'machinery-units', search] as const,
};

export function useMachineryUnits(farmId: string, search: string) {
  return useQuery({
    queryKey: unitKeys.list(farmId, search),
    queryFn: () =>
      api.get<UnitsPage>(
        `/api/v1/company/machinery-units?${new URLSearchParams(
          search.trim() ? { q: search.trim() } : {},
        )}`,
      ),
    staleTime: 15_000,
  });
}

export function useSaveUnit(farmId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: Record<string, unknown>) =>
      api.post<MachineryUnit>('/api/v1/company/machinery-units', payload),
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: farmCardKeys.all(farmId) });
    },
  });
}

/**
 * Набор операций сцепки.
 *
 * PATCH принимает состав целиком, поэтому вместе с операциями отправляем и
 * текущий состав — иначе «отметил операцию» молча разобрало бы сцепку.
 */
export function useSetUnitOperations(farmId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ unit, operationIds }: { unit: MachineryUnit; operationIds: string[] }) =>
      api.patch<MachineryUnit>(`/api/v1/company/machinery-units/${unit.id}`, {
        prime_model_id: unit.prime_mover?.model_id ?? null,
        members: unit.members.map((m) => ({ model_id: m.model_id, count: m.count })),
        ...(unit.name_is_custom ? { name: unit.name } : {}),
        operation_ids: operationIds,
      }),
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: farmCardKeys.all(farmId) });
    },
  });
}
